import React, {useState} from "react"
// MUI
import {Box, Button, Grid2 as Grid, Typography} from "@mui/material"
import {FileUploadOutlined, FileDownloadOutlined} from "@mui/icons-material"
// Translation
import {useTranslation} from "react-i18next"
// Components
import {DownloadSpreadsheetDialog} from "@/components/UI"
import {PageHeader, SectionCard} from "@/components/Layout"
import {packagingTokens as tk} from "@/theme/packagingTokens"
// Data
import {usePermissions} from "@/context/PermissionsProvider"
import {useUser} from "@/context/UserProvider"
import {parentOrigin} from "@/utils/parentOrigin"

// Batch management (Tools) — the single home of the two batch-level actions
// that used to be scattered across Dashboard, Brands & Ranges and Products:
// import a spreadsheet to create QR Codes in bulk, or export the existing
// products as a spreadsheet to review/modify them.
export default function BatchManagementTab() {

    const {t} = useTranslation(["common"])
    const {isAccountRole} = usePermissions()
    const {markStepDone} = useUser()

    const canCreate = !isAccountRole(["analyst"])
    const [openSpreadsheetDialog, setOpenSpreadsheetDialog] = useState<boolean>(false)

    // Batch CSV import lives in the parent dashboard (the Atlas wizard scoped
    // to the packaging template types) — ask it to open the dialog.
    const handleOpenImport = () => {
        // Getting-started: reaching the import wizard completes the batch-create step.
        markStepDone?.("create_products_batch")
        window.top?.postMessage(JSON.stringify(["import"]), parentOrigin)
    }

    const actionCard = (
        icon: React.ReactNode,
        title: string,
        desc: string,
        button: React.ReactNode,
    ) => (
        // Both action cards stretch to the tallest of the row.
        <SectionCard sx={{height: "100%", boxSizing: "border-box", display: "flex", flexDirection: "column"}}>
            <Box sx={{display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 1.5, flex: 1}}>
                <Box
                    sx={{
                        width: 40, height: 40, borderRadius: tk.radius.md,
                        backgroundColor: tk.color.green100, color: tk.color.greenDark,
                        display: "flex", alignItems: "center", justifyContent: "center",
                    }}
                >
                    {icon}
                </Box>
                <Typography sx={{fontSize: "15px", fontWeight: 700}}>{title}</Typography>
                <Typography sx={{fontSize: "13px", color: tk.color.textSecondary, flex: 1}}>{desc}</Typography>
                {button}
            </Box>
        </SectionCard>
    )

    return (
        <Box>
            <PageHeader title={t("nav_batch_management")} subtitle={t("batches_subtitle")} />

            <Grid container spacing={2.5} alignItems={"stretch"}>
                <Grid size={{xs: 12, md: 6}}>
                    {actionCard(
                        <FileUploadOutlined sx={{fontSize: 22}} />,
                        t("batches_import_title"),
                        t("batches_import_desc"),
                        <Button
                            variant={"contained"} disableElevation disabled={!canCreate}
                            data-step-target={"batch-import"}
                            onClick={handleOpenImport}
                            startIcon={<FileUploadOutlined sx={{fontSize: 16}} />}
                            sx={{textTransform: "none", fontWeight: 600, borderRadius: tk.radius.sm, px: 2}}
                        >
                            {t("create_batch")}
                        </Button>,
                    )}
                </Grid>
                <Grid size={{xs: 12, md: 6}}>
                    {actionCard(
                        <FileDownloadOutlined sx={{fontSize: 22}} />,
                        t("batches_export_title"),
                        t("batches_export_desc"),
                        <Button
                            data-step-target={"batch-export"}
                            onClick={() => setOpenSpreadsheetDialog(true)}
                            startIcon={<FileDownloadOutlined sx={{fontSize: 16}} />}
                            sx={{textTransform: "none", fontWeight: 600, borderRadius: tk.radius.sm, px: 2, color: tk.color.textPrimary, border: `1px solid ${tk.color.border}`, backgroundColor: tk.color.surface, "&:hover": {backgroundColor: tk.color.bg, borderColor: tk.color.borderStrong}}}
                        >
                            {t("download_excel_template")}
                        </Button>,
                    )}
                </Grid>
            </Grid>

            <DownloadSpreadsheetDialog
                open={openSpreadsheetDialog}
                onClose={() => setOpenSpreadsheetDialog(false)}
                {...{t}}
            />
        </Box>
    )
}
